import React, { useState, useEffect, useCallback } from "react";
import {
    Flex,
    Box,
    Stack,
    List,
    ListItem,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Select,
    Input,
    RadioGroup,
    Radio,
    Button,
    useToast,
    Spinner,
    Text,
    IconButton,
} from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";

const API_BASE_URL = "https://qaapirgeneng.digitalfields.co.za/ws1.cfc";

const Reports = ({ HrequestID }) => {
    // Parameters neccessary for making api requests
    const HuserID = localStorage.getItem("HuserID");
    const UUID = localStorage.getItem("UUID");

    // User feedback cues
    const [loading, setLoading] = useState(true);
    const toast = useToast();

    // Candidate name from getAllReportsPerClient endpoint
    const [reportTypes, setReportTypes] = useState([]);
    const [reports, setReports] = useState([]);
    const [reportMeasures, setReportMeasures] = useState([]);

    // State for managing form data
    const [formData, setFormData] = useState({
        reportType: "",
        reportFile: null,
        reportDate: "",
        reportMeasures: {},
    });

    const [errors, setErrors] = useState({
        reportType: "",
        reportFile: "",
        reportMeasures: ""
    });

    // Fetching Report Types For Client
    const getReportTypes = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${API_BASE_URL}?method=getAllReportsPerClient&huserID=${HuserID}&uuid=${UUID}&HclientID=11`,
                {
                    method: "GET",
                }
            );

            const data = await response.json();
            setReportTypes(data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    // Fetching Measures for selected report type
    const getReportMeasures = async (HreportID) => {
        try {
            setLoading(true);
            const response = await fetch(
                `${API_BASE_URL}?method=getMeasuresPerReport&huserID=${HuserID}&uuid=${UUID}&HreportID=${HreportID}`,
                {
                    method: "GET",
                }
            );

            const data = await response.json();
            
            // Render result capturing inputs
            const groups = data.reduce((acc, currentGroup) => {
                // Check if accumulator has measureGroup property of currentGroup
                if (!acc[currentGroup.measureGroup]) {
                    acc[currentGroup.measureGroup] = [];
                }
                
                // Parse the measure_data_json if it exists
                let options = [];
                if (currentGroup.measure_data_json) {
                    try {
                        options = JSON.parse(currentGroup.measure_data_json);
                    } catch (e) {
                        // Fallback to default options if parsing fails
                        options = [
                            { shortdescription: "2", longdescription: "2", DmeasurevaluesID: 2 },
                            { shortdescription: "1", longdescription: "1", DmeasurevaluesID: 1 },
                            { shortdescription: "0", longdescription: "0", DmeasurevaluesID: 0 }
                        ];
                    }
                }
                
                // Add the parsed options to the currentGroup
                currentGroup.parsedOptions = options;
                
                // Add that currentGroup object to the array of this group
                acc[currentGroup.measureGroup].push(currentGroup);
                return acc;
            }, {});

            const measureGroups = Object.entries(groups).map(
                ([groupName, measures]) => {
                    return { groupName, measures };
                }
            );

            setReportMeasures(measureGroups);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    // Fetching Reports For Request to populate report listing table
    const getReportsPerRequest = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${API_BASE_URL}?method=getReportsPerRequest&huserID=${HuserID}&uuid=${UUID}&HrequestID=${HrequestID}`,
                {
                    method: "GET",
                }
            );

            const data = await response.json();
            if (data.length) {
                setReports(data);
            } else {
                return;
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    // Posting form data for Report:
    const submitFormData = async (formDataBody) => {
        try {
            const response = await fetch(
                `${API_BASE_URL}?method=uploadExternalIntegratedReport&huserID=${HuserID}&uuid=${UUID}&HrequestID=${HrequestID}`,
                {
                    method: "POST",
                    body: formDataBody,
                }
            );

            if (!response.ok) {
                throw new Error("Failed to upload");
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    };

    const handleChange = (e) => {
        // Check if input change is the report type then fetch report measures
        if (e.target.name === "reportType") {
            let HreportID = e.target.value;
            setFormData({
                reportType: HreportID,
                reportFile: null,
                reportDate: "",
                reportMeasures: {}
            });
            setReportMeasures([]);
            getReportMeasures(HreportID);
        } else if (e.target.files) {
            let selectedFile = e.target.files[0];
            setFormData({
                ...formData,
                reportFile: selectedFile,
            });

            // Clear any previous error
            if (errors.reportFile) {
                setErrors({
                    ...errors,
                    reportFile: "",
                });
            }
        } else if (e.target.name === "reportDate") {
            setFormData({
                ...formData,
                reportDate: e.target.value,
            });
        }
    };

    const handleRadioChange = (measureId, value) => {
        const newValue = parseInt(value);
        setFormData(prevData => {
            const newMeasures = { ...prevData.reportMeasures };
            newMeasures[measureId] = newValue;
            return {
                ...prevData,
                reportMeasures: newMeasures
            };
        });
    };

    // Validate the form
    const validateForm = () => {
        const newErrors = {
            reportType: "",
            reportFile: "",
            reportMeasures: ""
        };

        let isValid = true;

        if (!formData.reportType) {
            newErrors.reportType = "Please select a report type";
            isValid = false;
        }

        if (!formData.reportFile) {
            newErrors.reportFile = "Please upload a file";
            isValid = false;
        }

        // Check if all measures have been answered
        if (reportMeasures.length > 0) {
            const allMeasuresAnswered = reportMeasures.every(group => 
                group.measures.every(measure => 
                    formData.reportMeasures[measure.DreportID] !== undefined
                )
            );

            if (!allMeasuresAnswered) {
                newErrors.reportMeasures = "Please answer all measures";
                isValid = false;
                toast({
                    title: "Missing Measures",
                    description: "Please answer all measures before submitting",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        }

        setErrors(newErrors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form before submission
        if (!validateForm()) {
            return;
        }

        // Create FormData object for the request
        const formDataBody = new FormData();
        formDataBody.append("HreportID", formData.reportType);
        formDataBody.append("uploadFile", formData.reportFile);
        formDataBody.append("reportDate", formData.reportDate);
        formDataBody.append("reportMeasures", JSON.stringify(formData.reportMeasures));

        setLoading(true);

        try {
            for (const entry of formDataBody.entries()) {
                console.log(entry[0], entry[1]); // Key and value
            }

            // Use the separate function for the POST request
            const result = await submitFormData(formDataBody);

            toast({
                title: "Submission successful",
                description: "Your report has been submitted successfully.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            // Reset the forms state object
            setFormData({
                reportType: "",
                reportFile: null,
                reportDate: "",
                reportMeasures: {},
            });

            // Reset the file input by clearing its value
            const fileInput = document.getElementById("file-upload");
            if (fileInput) {
                fileInput.value = "";
            }
        } catch (error) {
            toast({
                title: "Report Submission Failed",
                description:
                    error.message || "An error occurred during submission.",
                status: "error",
                duration: 6000,
                isClosable: true,
            });
            console.error("Error submitting form:", error);
        } finally {
            setLoading(false);
        }
    };

    // Runs when download icon for report url is clicked
    const handleDownload = (url) => {
        window.open(url, "_blank");
    };

    // Add cleanup effect
    useEffect(() => {
        return () => {
            // Reset form data when component unmounts
            setFormData({
                reportType: "",
                reportFile: null,
                reportDate: "",
                reportMeasures: {}
            });
            setReportMeasures([]);
        };
    }, []);

    // Add effect to reset measures when report type changes
    useEffect(() => {
        if (!formData.reportType) {
            setReportMeasures([]);
        }
    }, [formData.reportType]);

    useEffect(() => {
        getReportTypes();
        getReportsPerRequest();
    }, []);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="10vh"
            >
                <Spinner size="xl" />
            </Box>
        );
    }

    return (
        <Flex direction="column" p={4} w="100%">
            {/* Form for submitting report */}
            <Flex
                as="form"
                onSubmit={handleSubmit}
                border="2px"
                borderColor="gray.100"
                rounded="md"
                mb="4"
                p="4"
                justify="space-around"
                direction={{ base: "column", xl: "row" }}
            >
                <Box flex="3">
                    {/* Report Type selection input */}
                    <FormControl
                        isRequired
                        isInvalid={!!errors.reportType}
                        my="4"
                    >
                        <FormLabel fontWeight="bold">Report Type</FormLabel>
                        <Select
                            name="reportType"
                            placeholder="Select report type"
                            value={formData.reportType}
                            onChange={handleChange}
                        >
                            {reportTypes.map((reportType) => (
                                <option key={reportType.HreportID} value={reportType.HreportID}>
                                    {reportType.reportName}
                                </option>
                            ))}
                        </Select>
                        <FormErrorMessage>{errors.reportType}</FormErrorMessage>
                    </FormControl>

                    {/* Report file upload input */}
                    <FormControl
                        isRequired
                        isInvalid={!!errors.reportFile}
                        my="4"
                    >
                        <FormLabel fontWeight="bold">
                            Upload Report File
                        </FormLabel>
                        <Input
                            type="file"
                            id="file-upload"
                            name="reportFile"
                            onChange={handleChange}
                            p={1}
                            accept="image/*,application/pdf"
                        />
                        <FormErrorMessage>{errors.reportFile}</FormErrorMessage>
                    </FormControl>

                    {/* Date selection input */}
                    <FormControl isRequired my="4">
                        <FormLabel fontWeight="bold" my="4">
                            Report Submission Date
                        </FormLabel>
                        <Input
                            type="date"
                            name="reportDate"
                            value={formData.reportDate}
                            onChange={handleChange}
                            isRequired
                        />
                    </FormControl>
                </Box>
                <Box ml="4" flex="7">
                    {/* Report Results input section */}
                    {reportMeasures.map((group, groupIndex) => (
                        <Box key={`group-${groupIndex}`} p="6" my="6" bg="lightGray" rounded="md">
                            <Text fontWeight="extrabold" fontSize="xl" mb={4} color="gray.700">
                                {group.groupName}
                            </Text>
                            <Box>
                                {group.measures.map((measure, measureIndex) => {
                                    const currentValue = formData.reportMeasures[measure.DreportID];
                                    return (
                                        <FormControl 
                                            key={measure.DreportID}
                                            isRequired
                                            isInvalid={errors.reportMeasures && !currentValue}
                                            bg={measureIndex % 2 === 0 ? "white" : "gray.50"}
                                            p={3}
                                            borderRadius="md"
                                        >
                                            <Flex justify="space-between" align="center" mb={2}>
                                                <Box flex="1" pr={4}>
                                                    <FormLabel fontWeight="bold" mb={0} fontSize="md">
                                                        {measure.measureDisplay}
                                                    </FormLabel>
                                                </Box>
                                                <Box flex="1" pl={4}>
                                                    <Stack direction="row" spacing={4} justify="flex-start">
                                                        {measure.parsedOptions && measure.parsedOptions.map((option, index) => (
                                                            <Radio 
                                                                key={`${measure.DreportID}-${option.DmeasurevaluesID}-${index}`}
                                                                isChecked={currentValue === option.DmeasurevaluesID}
                                                                onChange={() => handleRadioChange(measure.DreportID, option.DmeasurevaluesID)}
                                                                value={option.DmeasurevaluesID.toString()}
                                                                title={option.longdescription}
                                                            >
                                                                {option.shortdescription}
                                                            </Radio>
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            </Flex>
                                            {errors.reportMeasures && !currentValue && (
                                                <FormErrorMessage>This measure is required</FormErrorMessage>
                                            )}
                                        </FormControl>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))}

                    {/* Form submit button */}
                    <Button type="submit" my="6">
                        Submit
                    </Button>
                </Box>
            </Flex>

            {/* Report Listing */}
            <List>
                <ListItem
                    border="2px"
                    borderColor="gray.100"
                    borderTopRadius="md"
                >
                    <Flex>
                        <Text w="30%" fontWeight="bold" p="2">
                            Date
                        </Text>
                        <Text w="10%" fontWeight="bold" p="2">
                            Download Link
                        </Text>
                        <Text w="30%" fontWeight="bold" p="2">
                            Report Name
                        </Text>
                        <Text w="30%" fontWeight="bold" p="2">
                            Uploaded By
                        </Text>
                    </Flex>
                </ListItem>

                {reports.map((report, index) => (
                    <ListItem
                        key={`report-${index}`}
                        borderBottom="2px"
                        borderLeft="2px"
                        borderRight="2px"
                        borderColor="gray.100"
                        borderBottomRadius="md"
                    >
                        <Flex>
                            <Text w="30%" variant={"tableBodyCell"} p="2">
                                {report.dateon}
                            </Text>
                            <Box w="10%" p="2">
                                <IconButton
                                    icon={<DownloadIcon />}
                                    onClick={() =>
                                        handleDownload(report.downLoadLink)
                                    }
                                ></IconButton>
                            </Box>
                            <Text w="30%" variant={"tableBodyCell"} p="2">
                                {report.reportName}
                            </Text>
                            <Text w="30%" variant={"tableBodyCell"} p="2">
                                {report.uploadedBy}
                            </Text>
                        </Flex>
                    </ListItem>
                ))}
            </List>
        </Flex>
    );
};

export default Reports;
